import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import DateRangeFilter from "../../components/dateRangeFilter";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import AddButton from "../../components/addButton";
import { useGetCashHistoryQuery } from "../../services/cash";
import { useGetBranchesQuery } from "../../services/branches";
import { AddCashRoute } from "../../routes/routepath";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface CashRecord {
  key:          string;
  date?:        string;
  time?:        string;
  branch?:      string;
  amount?:      number;
  type?:        string;
  collectedBy?: string;
  createdBy?:   string;
  remark?:      string;
}

interface DateRangeValue { start: Date; end: Date; }

/* ─── Constants ──────────────────────────────────────────────────────── */

const defaultDateRange = (): DateRangeValue => ({
  start: dayjs().subtract(14, 'day').startOf('day').toDate(),
  end:   dayjs().endOf('day').toDate(),
});

const renderAmount = (v?: number, record?: CashRecord) => {
  if (v === undefined || v === null) return '-';
  const isDebit = record?.type === 'debit';
  return (
    <span style={{ color: isDebit ? '#ff4d4f' : '#52c41a' }}>
      {isDebit ? '−' : ''}₹{v.toLocaleString('en-IN')}
    </span>
  );
};

const renderDate = (v?: string, record?: CashRecord) => {
  if (!v) return '-';
  const dt = new Date(v);
  const dateLabel = isNaN(dt.getTime()) ? v : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <div>
      <div>{dateLabel}</div>
      {record?.time && <div style={{ fontSize: 12, opacity: 0.65 }}>{record.time}</div>}
    </div>
  );
};

/* ─── Component ──────────────────────────────────────────────────────── */

const CashHistory = () => {
  const [page,      setPage]      = useState(1);
  const [limit,     setLimit]     = useState(10);
  const [dateRange, setDateRange] = useState<DateRangeValue | null>(defaultDateRange);

  // Branch comes from the header's universal branch filter — no separate
  // filter on this page. GET /cash/:branchId/history requires a branchId in
  // the path (no "all branches" variant exists yet), so when the header has
  // nothing selected we fall back to the first branch instead of going blank.
  const rawBranch = useSelector((state: any) => state.branch.selectedBranch);
  const headerBranchId = typeof rawBranch === 'object' ? (rawBranch?._id || rawBranch?.id) : rawBranch;

  const { data: branchesResp } = useGetBranchesQuery();
  const branchList = Array.isArray(branchesResp)
    ? branchesResp
    : (Array.isArray((branchesResp as any)?.data) ? (branchesResp as any).data : []);
  const fallbackBranchId = branchList[0]?._id || branchList[0]?.id;

  const selectedBranchId = headerBranchId || fallbackBranchId;

  useEffect(() => { setPage(1); }, [selectedBranchId]);

  const { data: historyResp, isFetching } = useGetCashHistoryQuery(
    {
      branchId: selectedBranchId as string,
      startDate: dateRange?.start ? dayjs(dateRange.start).format('YYYY-MM-DD') : undefined,
      endDate:   dateRange?.end   ? dayjs(dateRange.end).format('YYYY-MM-DD')   : undefined,
      page,
      limit,
    },
    { skip: !selectedBranchId },
  );

  const apiData = historyResp as any;

  const records = useMemo<CashRecord[]>(() => {
    const rows: any[] = apiData?.data || [];
    return rows.map((c: any) => ({
      key:         c._id,
      date:        c.date,
      time:        c.time,
      branch:      c.branchId?.name,
      amount:      c.amount,
      type:        c.type,
      collectedBy: c.collectedBy?.name,
      createdBy:   c.createdBy?.name,
      remark:      c.remark,
    }));
  }, [apiData]);

  const columns = [
    { title: 'Date',         dataIndex: 'date',        key: 'date',        render: renderDate },
    { title: 'Branch',       dataIndex: 'branch',      key: 'branch',      render: (v?: string) => v || '-' },
    { title: 'Amount',       dataIndex: 'amount',      key: 'amount',      render: (v?: number, record?: CashRecord) => renderAmount(v, record) },
    { title: 'Collected By', dataIndex: 'collectedBy', key: 'collectedBy', render: (v?: string) => v || '-' },
    { title: 'Added By',     dataIndex: 'createdBy',   key: 'createdBy',   render: (v?: string) => v || '-' },
    { title: 'Remark',       dataIndex: 'remark',      key: 'remark',      render: (v?: string) => v || '-' },
  ];

  return (
    <div className="cash-history-page">
      <div className="header-section">
        <h2 style={{ margin: 0 }}>Cash History</h2>
        <div className="right-col">
          <DateRangeFilter
            value={dateRange}
            onChange={(v) => { setDateRange(v); setPage(1); }}
          />
          <AddButton to={AddCashRoute}>Add Cash</AddButton>
        </div>
      </div>

      <div className="cash-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={records}
          loading={isFetching}
          pagination={false}
          rowKey={(record: CashRecord) => record.key}
          rowClassName={(record: CashRecord) =>
            record.type === 'debit' ? 'cash-row-debit' : record.type === 'credit' ? 'cash-row-credit' : ''
          }
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={apiData?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />
    </div>
  );
};

export default CashHistory;
